/**
 * Thin fetch wrapper for the Hub API. All client-side queries /
 * mutations go through here so auth + error handling have one
 * place to live. Same-origin in production; the Vite dev server
 * proxies `/api/*` to the Hono backend (see `vite.config.ts`).
 *
 * `credentials: "include"` is required so the `authjs.session-token`
 * cookie issued by the Next-side sign-in survives across the proxy.
 */

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

/**
 * The single place client code turns a thrown request failure into a
 * human string for a toast / inline banner. Nearly every component used
 * to carry its own `errorMessage` / `errorText` / `errMessage` copy of
 * this; they all dug the same shape.
 *
 * Order of preference:
 *   1. `detail.detail` — a nested server message string (Zod / handlers).
 *   2. `detail.error`   — the top-level server `error` string. (This one
 *      `ApiError.message` already folds in at the throw site, so plain
 *      `err.message` usually suffices — but a caller may hold the raw
 *      `detail`.)
 *   3. `fallback`       — the caller's friendly line ("Save failed."),
 *      preferred over the bare `METHOD /path → status` message so a
 *      user never sees a raw status line in a toast.
 *   4. `err.message`    — surfaced only when no fallback was given.
 */
export function apiErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ApiError) {
    const d = err.detail;
    if (d && typeof d === "object") {
      const obj = d as { error?: unknown; detail?: unknown };
      if (typeof obj.detail === "string") return obj.detail;
      if (typeof obj.error === "string") return obj.error;
    }
    return fallback ?? err.message;
  }
  if (err instanceof Error) return fallback ?? err.message;
  return fallback ?? "Something went wrong. Please try again.";
}

/**
 * How long an idempotent GET keeps retrying after a *network* failure
 * (`fetch` rejects with a `TypeError` — no HTTP response) or a proxy
 * answering for a server that isn't there (502/503/504). Covers blips like
 * `ERR_NETWORK_CHANGED` and, above all, a deploy: the app container is
 * replaced and is gone for half a minute, and a loader firing then should
 * wait it out instead of erroring the page. Only GETs retry — mutations
 * must not be silently replayed.
 */
const GET_RETRY_WINDOW_MS = 45_000;
const RETRY_BASE_MS = 300;
const RETRY_MAX_MS = 5_000;

/** Statuses the proxy gives while the app itself is down or restarting. */
const RESTARTING_STATUSES = new Set([502, 503, 504]);

/**
 * Hard ceiling on a single request. A hung fetch (server stall, lost
 * network, saturated proxy) must not occupy a browser connection slot
 * forever — the browser only allows a handful of concurrent
 * connections per host, so a pile of pending requests starves
 * everything else (notably the `/api/me/layout` loader) and the whole
 * app dies with "Could not load layout". A bounded request aborts,
 * frees the slot, and (for GETs) retries.
 */
const REQUEST_TIMEOUT_MS = 12_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A `fetch` rejection (vs. a non-2xx response) is a network-level
 *  failure: no response was received, so retrying is safe + sensible.
 *  Our own timeout surfaces as an `AbortError` — treat it the same. */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof DOMException && err.name === "AbortError");
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  retryUntil: number | null = null,
  timeoutMs = REQUEST_TIMEOUT_MS,
  attempt = 0,
): Promise<T> {
  const retryDelay = () => {
    if (retryUntil === null) return null;
    const delay = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
    return Date.now() + delay < retryUntil ? delay : null;
  };
  const init: RequestInit = {
    method,
    credentials: "include",
  };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(path, { ...init, signal: controller.signal });
  } catch (err) {
    // Transient network failure (or our own timeout) — retry
    // idempotent requests a few times with linear backoff before
    // giving up.
    const delay = isNetworkError(err) ? retryDelay() : null;
    if (delay !== null) {
      await sleep(delay);
      return request<T>(method, path, body, retryUntil, timeoutMs, attempt + 1);
    }
    // Normalize our timeout-abort to a plain network error so callers
    // (the layout error screen) render the recoverable "Connection
    // lost" state rather than a raw AbortError.
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new TypeError(`${method} ${path} timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
  if (!res.ok && RESTARTING_STATUSES.has(res.status)) {
    const delay = retryDelay();
    if (delay !== null) {
      await sleep(delay);
      return request<T>(method, path, body, retryUntil, timeoutMs, attempt + 1);
    }
  }
  if (!res.ok) {
    const text = await res.text();
    let detail: unknown = text;
    try {
      detail = JSON.parse(text);
    } catch {
      // text body
    }
    // Prefer the server's own `error` string ("This entry is on a
    // report…", "…fixed-price work item — billing is locked…") over
    // the bare method/path/status — toasts across the app render
    // `err.message`, and the generic form tells the user nothing.
    const serverMessage =
      typeof detail === "object" &&
      detail !== null &&
      "error" in detail &&
      typeof (detail as { error: unknown }).error === "string"
        ? (detail as { error: string }).error
        : undefined;
    const restarting = RESTARTING_STATUSES.has(res.status) && serverMessage === undefined;
    throw new ApiError(
      res.status,
      serverMessage ??
        (restarting
          ? "Hub is restarting (probably an update going out). Nothing was lost, try again in a moment."
          : `${method} ${path} → ${res.status}`),
      detail,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Per-call overrides. `timeoutMs` raises the default 12 s ceiling for the
 *  rare long-running endpoint (e.g. AI analysis, which waits on Claude). */
type RequestOpts = { timeoutMs?: number };

export const api = {
  get: <T>(path: string, opts?: RequestOpts) =>
    request<T>("GET", path, undefined, Date.now() + GET_RETRY_WINDOW_MS, opts?.timeoutMs),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("POST", path, body, null, opts?.timeoutMs),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("PATCH", path, body, null, opts?.timeoutMs),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("PUT", path, body, null, opts?.timeoutMs),
  delete: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>("DELETE", path, body, null, opts?.timeoutMs),
};
